import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Banknote,
  Users,
  AlertTriangle,
  TrendingUp,
  Loader2,
} from "lucide-react";
import { useFinancialStats } from "../adapters/Requests";

const MinimalTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white px-3 py-2 rounded border border-gray-100 shadow-lg z-50">
        <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">
          {payload[0].name}
        </p>
        <p className="text-sm font-bold text-gray-900">
          {typeof payload[0].value === "number" && payload[0].value > 1000
            ? `₦${payload[0].value.toLocaleString()}`
            : payload[0].value.toLocaleString()}
        </p>
      </div>
    );
  }
  return null;
};

const ChartCard = ({
  title,
  children,
  footerData,
  link,
  linkText,
  hasData,
}) => (
  <div className="bg-white border border-gray-200 rounded-lg p-5 flex flex-col h-full shadow-sm hover:shadow-md transition-shadow duration-200">
    <div className="mb-4 flex justify-between items-center">
      <h3 className="text-sm font-bold text-gray-700">{title}</h3>
      {link && (
        <Link
          to={link}
          className="text-[10px] font-bold text-emerald-600 hover:text-emerald-700 uppercase tracking-wide flex items-center gap-1"
        >
          {linkText} <TrendingUp size={12} />
        </Link>
      )}
    </div>
    <div className="flex-1 min-h-[180px] w-full relative">
      {!hasData ? (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-300">
          No financial data for this period
        </div>
      ) : (
        <ResponsiveContainer
          width="100%"
          height="100%"
          className="!outline-none"
        >
          {children}
        </ResponsiveContainer>
      )}
    </div>
    {footerData && (
      <div className="mt-4 pt-3 border-t border-gray-50 flex flex-wrap gap-3 justify-center">
        {footerData.map((item, idx) => (
          <div key={idx} className="flex items-center gap-1.5">
            <span
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: item.color }}
            />
            <span className="text-[10px] font-semibold text-gray-500 uppercase">
              {item.name}
            </span>
            <span className="text-[10px] font-bold text-gray-800">
              {item.value}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

function SmallStatGrid({ stats }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
      {stats.map((stat, index) => (
        <div
          key={index}
          className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 bg-slate-50 shadow-sm cursor-default select-none h-24 transition-colors ${
            stat.alert ? "border-red-100" : "border-white"
          }`}
        >
          <div
            className={`h-7 w-7 rounded-md flex items-center justify-center mb-2 ${
              stat.alert ? "text-red-500" : "text-emerald-500"
            }`}
          >
            <stat.icon size={18} strokeWidth={2.5} />
          </div>
          <h3
            className={`text-lg font-bold leading-none ${
              stat.alert ? "text-red-700" : "text-slate-700"
            }`}
          >
            {stat.value}
          </h3>
          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mt-1.5">
            {stat.label}
          </p>
        </div>
      ))}
    </div>
  );
}

export default function ManagerAnalytics({ dateRange }) {
  const { data, isLoading } = useFinancialStats(dateRange);

  if (isLoading) {
    return (
      <div className="w-full h-64 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
      </div>
    );
  }

  const stats = [
    {
      label: "Total Payable",
      value: `₦${((data?.totalPayable || 0) / 1000).toFixed(0)}k`,
      icon: Banknote,
    },
    {
      label: "Processed",
      value: data?.totalProcessed || 0,
      icon: Users,
    },
    {
      label: "Full Stipend",
      value: data?.paymentTypes?.find((t) => t.name === "Full")?.value || 0,
      icon: TrendingUp,
    },
    {
      label: "Bank Issues",
      value: data?.bankIssues || 0,
      icon: AlertTriangle,
      alert: (data?.bankIssues || 0) > 0,
    },
  ];

  const typeColors = { Full: "#10b981", Half: "#f59e0b", None: "#ef4444" };

  const paymentTypeData = (data?.paymentTypes || [])
    .filter((t) => t.value > 0)
    .map((t) => ({
      ...t,
      color: typeColors[t.name] || "#9ca3af",
    }));

  const deptCostData = data?.departmentCosts || [];

  const axisStyle = { fill: "#9ca3af", fontSize: 10, fontWeight: 500 };
  const gridStyle = { stroke: "#f3f4f6", strokeDasharray: "3 3" };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">
          Financial Endorsements
        </h2>
      </div>

      <SmallStatGrid stats={stats} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <ChartCard
            title="Cost by Department (Top 5)"
            hasData={deptCostData.length > 0}
            link="/report/payment-advice"
            linkText="View Report"
            data={deptCostData}
          >
            <BarChart
              data={deptCostData}
              margin={{ top: 10, right: 0, left: -20, bottom: 0 }}
            >
              <CartesianGrid {...gridStyle} vertical={false} />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={axisStyle}
                dy={10}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={axisStyle}
                allowDecimals={false}
                tickFormatter={(val) => `₦${val / 1000}k`}
              />
              <Tooltip
                content={<MinimalTooltip />}
                cursor={{ fill: "transparent" }}
              />
              <Bar
                dataKey="value"
                fill="#34d399"
                radius={[4, 4, 0, 0]}
                barSize={40}
              />
            </BarChart>
          </ChartCard>
        </div>

        <div className="lg:col-span-1">
          <ChartCard
            title="Payment Classification"
            hasData={paymentTypeData.length > 0}
            footerData={paymentTypeData}
          >
            <PieChart>
              <Pie
                data={paymentTypeData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                stroke="none"
              >
                {paymentTypeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<MinimalTooltip />} />
            </PieChart>
          </ChartCard>
        </div>
      </div>
    </div>
  );
}
